import type { BehaviorData } from '../behaviordata';
import type { Color } from '../common';

// Re-export legacy types for migration support
export {
    ParticleSystemCurveMode,
    LegacyGradientMode,
} from '../legacy/particledata-legacy';
export type {
    LegacyParticleSystemShapeType,
    LegacyMinMaxCurve,
    LegacyGradientAttributes,
    LegacyParticleSystemData,
} from '../legacy/particledata-legacy';

/**
 * Shape type for particle emission volume
 */
export enum ParticleShapeType {
    Cone = "Cone",
    Sphere = "Sphere",
    Box = "Box"
}

/**
 * Import mode for a particle texture — an AUTHORING-TIME concept only.
 *
 * It tells the editor how to convert a source image to premultiplied RGBA. It is NOT
 * stored in scene data: only the baked premultiplied `texture` is persisted, and at
 * runtime all particles use premultiplied Porter-Duff blend unconditionally. The original
 * source + chosen mode live in editor session memory only (see `editor/particleTexture`
 * `ParticleSource`, `docs-internal/particle-texture-format.md`).
 *
 * Conversion math lives in `editor/particleTexture.ts` (`premultiplyPixels`).
 */
export enum ParticleTextureMode {
    /** Additive glow texture (JPG, opaque PNG). Editor derives α from luminance. */
    Additive = "Additive",
    /** Transparent texture with alpha (smoke, dust, leaves). Editor premultiplies RGB by alpha. */
    Alpha = "Alpha",
    /** Already premultiplied RGBA. No conversion needed. */
    Premultiplied = "Premultiplied",
}

/**
 * @deprecated Use ParticleTextureMode instead. Kept for legacy scene data migration.
 */
export enum ParticleBlendMode {
    Additive = "Additive",
    Alpha = "Alpha"
}

/**
 * Min/Max range for randomized values.
 * When min === max, treated as a constant value.
 */
export interface Range {
    min: number;
    max: number;
}

/**
 * Keyframe for curves over particle lifetime.
 * @property time - Normalized lifetime position (0-1)
 * @property value - Multiplier value at this time
 */
export interface Keyframe {
    time: number;
    value: number;
}

/**
 * Shape configuration for particle emission
 */
export interface ParticleShape {
    type: ParticleShapeType;

    /** Radius for Cone and Sphere shapes (world units) */
    radius?: number;

    /** Angle for Cone shape (degrees, 0-90) */
    angle?: number;

    /** Box dimensions [x, y, z] for Box shape (world units) */
    boxSize?: [number, number, number];

    /**
     * Emission direction for Box shape as a unit vector [x, y, z].
     * Defaults to [0, 1, 0] (local Y). Unity Rectangle emits along [0, 0, 1] (local Z).
     */
    emitDirection?: [number, number, number];
}

/**
 * Simplified particle system data.
 * All particles operate in world space (independent after spawn).
 */
export interface ParticleSystemData extends BehaviorData {
    "@type": "ParticleSystem";

    /**
     * Particles emitted per second
     * @default 50
     */
    emissionRate: number;

    /**
     * Maximum particles alive at once (performance cap)
     * @default 1000
     */
    maxParticles: number;

    /**
     * Particle lifetime range in seconds
     * @default { min: 1, max: 2 }
     */
    lifetime: Range;

    /**
     * Starting color (#RRGGBBAA hex)
     * @default "#FFCC00FF"
     */
    startColor: Color;

    /** Optional second color for random range between startColor and startColor2 */
    startColor2?: Color;

    /**
     * Color particles fade to at end of life (#RRGGBBAA hex).
     * "#00000000" = fade to fully transparent (alpha 00).
     * "#000000FF" = fade to opaque black. "#FF0000FF" = fade to red.
     * With premultiplied blending, RGB channels have no effect when alpha is 0.
     */
    endColor?: Color;

    /**
     * Starting speed range (world units/second)
     * @default { min: 2, max: 4 }
     */
    startSpeed: Range;

    /**
     * Gravity multiplier (-1 = normal gravity down, 0 = no gravity, 1 = reverse)
     * @default 0.3
     */
    gravityModifier: number;

    /**
     * Emission shape configuration
     * @default { type: "Cone", radius: 0.05, angle: 30 }
     */
    shape: ParticleShape;

    /**
     * Particle texture asset path (local or URL). ALWAYS premultiplied RGBA — the editor
     * bakes the source to premultiplied on import (see ParticleTextureMode). Runtimes load
     * this directly and blend with premultiplied Porter-Duff; they never convert it.
     * @asset image
     * @default ""
     */
    texture: string;

    /**
     * Overall particle size multiplier applied on top of sizeOverLifetime keyframes.
     * Distinct from the object's transform scale which also affects emission shape.
     */
    particleScale?: number;

    /**
     * Size multiplier over lifetime (array of keyframes, time 0-1).
     * Omit for constant size.
     */
    sizeOverLifetime?: Keyframe[];

    /** Starting rotation range (degrees) */
    startRotation?: Range;

    /** Angular velocity range (degrees/second) */
    angularVelocity?: Range;

    /**
     * Noise-based turbulence applied to particle velocity.
     * Omit or set strength to 0 to disable.
     */
    noise?: ParticleNoise;
}

/**
 * Simple noise/turbulence parameters for wind-like effects.
 * Cross-engine compatible: maps to Perlin noise in both Unity and Babylon.
 */
export interface ParticleNoise {
    /** Strength of turbulence on each axis (world units). Equal XYZ = uniform. */
    strength: [number, number, number];

    /** Noise evolution speed — higher = more chaotic, lower = smoother swirls. Default 0.5 */
    frequency?: number;

    /** Number of noise octaves (1-4). More = finer detail. Default 2 */
    octaves?: number;

    /**
     * Velocity damping per second (0-1). Controls how quickly noise-induced
     * velocity decays. 0 = no damping (force accumulates), 1 = full damping
     * (position-offset behavior, like Unity). Default 0.
     */
    drag?: number;
}

/**
 * Legacy fallback values used by V1→V2 migration code.
 * These match the old Unity-era defaults, NOT the current JSDoc @default values.
 * Only used in `ParticleSystem.migrateLegacyData` when legacy fields are missing.
 */
export const LEGACY_PARTICLE_FALLBACKS = {
    emissionRate: 10,
    maxParticles: 100,
    lifetime: { min: 1, max: 2 },
    startColor: "#FFFFFFFF" as Color,
    startSpeed: { min: 1, max: 2 },
    gravityModifier: 0,
    shape: {
        type: ParticleShapeType.Cone,
        radius: 0.5,
        angle: 25
    } as ParticleShape,
    texture: ""
} as const;
